import React, { useRef, useEffect, useState } from "react";
import { useReactToPrint } from "react-to-print";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useSignalR } from "../../../context/SignalRContext";
import { useTranslation } from "react-i18next";
import ApiCall from "../../../Apicall/ApiCall";
import { getToken } from "../../../Compo/utilis/getToken";
import { AUTH_API_BASE_URL } from "../../../config/api-config";
import PublicPage from "../../PublicPage";
import TransactionHeader from "./TransactionHeader";
import ContractDocumentView, { CONTRACT_PRINT_PAGE_STYLE } from "./ContractDocumentView";
import { resolveBuyerName } from "./SellerProfileFlow";

// Read-only, ds-styled purchase agreement/contract document — the record view
// reached after both parties sign. Fetches the same ContractMain DTO and hits
// the same print/download-PDF endpoints as before; only the presentation changed.
// The document itself (the nine clauses) lives in ContractDocumentView, shared
// with the transaction record's "View the contract" preview modal so both
// surfaces render the exact same contract.
function ContractAgreementFlow() {
  const { t } = useTranslation();
  const { id } = useParams();
  const printRef = useRef(null);

  const [contractData, setContractData] = useState(null);
  const [buyerName, setBuyerName] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const handlePrint = useReactToPrint({ contentRef: printRef, documentTitle: "Contract", pageStyle: CONTRACT_PRINT_PAGE_STYLE });

  const handleDownloadPdf = async (lang = "sv") => {
    try {
      setDownloading(true);
      const token = getToken();
      const res = await axios.get(`${AUTH_API_BASE_URL}/ContractPdf/Download`, {
        params: { id, lang },
        responseType: "blob",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = lang === "en" ? "EN" : "SV";
      a.download = `Klargo-Contract-${contractData?.carInfoRegistrationNo || id}-${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("PDF download failed", e);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const fetchContract = async () => {
      try {
        const response = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: id },
        });

        if (response?.data?.result) {
          const result = response.data.result;
          setContractData(result);
          setLoading(false);
          resolveBuyerName(result).then(setBuyerName);

          // Pre-generate + store both language PDFs for a completed (signed) deal
          if (result.dealComplete) {
            try {
              const token = getToken();
              axios.post(`${AUTH_API_BASE_URL}/ContractPdf/Prewarm`, null, {
                params: { id },
                headers: { Authorization: token ? `Bearer ${token}` : "" },
              });
            } catch (_) {
              /* best-effort pre-store; on-demand download always works */
            }
          }
        }
      } catch (error) {
        console.error("Contract API Error:", error);
        setLoading(false);
      }
    };

    fetchContract();
  }, [id]);

  // Stay in the deal's SignalR group while a deal that is still running is open here. Everything
  // that must hold while the two parties are connected - the counterpart's sign-out reaching this
  // tab, the "Leave the deal?" guard, the expiry notice, the header countdown - is driven by
  // DealSessionManager from the connected deal id. This page is where both parties land after
  // signing and it never joined, so on a reload (or opening the link directly) all of that was
  // silently inert. A fully signed deal is closed and cannot expire, so only an open one is joined.
  const { dealId: connectedDealId, connectToDeal } = useSignalR();
  const joiningDeal = useRef(null);
  useEffect(() => {
    if (!id || contractData?.dealState !== "in_progress") return;
    if (String(connectedDealId) === String(id) || joiningDeal.current === id) return;
    joiningDeal.current = id;
    connectToDeal(id)
      .catch(() => {
        /* the page still works; only the live deal-session rules are missed */
      })
      .finally(() => {
        joiningDeal.current = null;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, contractData?.dealState, connectedDealId]);

  return (
    <PublicPage>
      <div className="flex min-h-screen flex-col bg-ds-canvas">
        <TransactionHeader />
        <main className="ds-scope w-full flex-1 px-4 pb-16 pt-6 md:px-6">
          <div className="ds-form mx-auto w-full">
            <div className="mb-4 flex items-center justify-end gap-2" style={{ maxWidth: "var(--ds-doc-measure)", marginInline: "auto" }}>
              <button type="button" onClick={handlePrint} disabled={loading} className="ds-action-ghost !px-4 !py-2 text-[13px]">
                {loading ? t("agreement.loading") : t("agreement.print")}
              </button>
              <button
                type="button"
                onClick={() => handleDownloadPdf("sv")}
                disabled={loading || downloading}
                className="ds-action-ghost !px-4 !py-2 text-[13px]"
              >
                {downloading ? t("agreement.downloading") : t("agreement.downloadSv")}
              </button>
              <button
                type="button"
                onClick={() => handleDownloadPdf("en")}
                disabled={loading || downloading}
                className="ds-action-ink !px-4 !py-2 text-[13px]"
              >
                {downloading ? t("agreement.downloading") : t("agreement.downloadEn")}
              </button>
            </div>

            {/* react-to-print clones only this node into a bare print iframe,
                carrying along the copied <style> tags but no real ancestors —
                without its own "klargo-public" class (normally inherited from
                PublicPage's wrapper) every selector in public.css, all scoped
                to ".klargo-public ...", fails to match in the printed copy.
                .klargo-public is also the rewritten `body` rule
                (background-color: var(--ds-canvas)) — bg-transparent cancels
                that one declaration so this node doesn't paint a second
                canvas-coloured box (harmless here, same colour as the page
                behind it, but kept consistent with the preview modal). */}
            <div ref={printRef} className="klargo-public bg-transparent">
              {loading ? (
                <div
                  className="overflow-hidden rounded-[13px] border border-ds-rule-edge bg-ds-sheet p-2"
                  style={{ maxWidth: "var(--ds-doc-measure)", marginInline: "auto" }}
                >
                  {Array.from({ length: 10 }).map((_, idx) => (
                    <div key={idx} className="mx-3 my-2 h-4 animate-pulse rounded bg-[rgba(20,36,46,.08)]" style={{ width: "60%" }} />
                  ))}
                </div>
              ) : (
                <ContractDocumentView t={t} contractData={contractData} buyerName={buyerName} />
              )}
            </div>
          </div>
        </main>
      </div>
    </PublicPage>
  );
}

export default ContractAgreementFlow;
